import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import { getCreatorHealthStats } from 'state/utils';
import InlineTable from '../primitives/InlineTable';
import { buildBreadcrumb } from '../lib/breadcrumbFormat';
import {
    type ColumnSpec,
    buildColumns,
    buildRows,
    parentName,
} from '../lib/tableHelpers';
import type {
    RawRow,
    RowBadge,
    TableModel,
    VisualProps,
    VisualWidget,
} from '../types';

const NAME_KEY = 'creator_name';
const TYPE_LABEL = 'Creator';

// Keys reference metrics.js so a rename there propagates here (and to parity with
// the explore creators table) instead of silently diverging.
const M = TIKTOK_METADATA_BY_KEY;

// Sortable creator columns: when one of these is the active sort, it becomes the
// second column; any other sort falls back to the default secondary.
const SORTABLE_KEYS = new Set([
    M.nb_likes_last_week.key,
    M.nb_likes_all.key,
    M.nb_views_last_week.key,
    M.nb_views_all.key,
    M.engagement_prc_all.key,
    M.paid_posts_views_prc_all.key,
    M.eligible_for_commission_posts_views_prc_all.key,
]);

// Dynamic columns: the first column is always likes latest week (the volume + the
// default sort); the second column is the active sort metric, or likes all time
// when sorting by the volume column / when no explicit sort was chosen.
const COLUMN_SPEC: ColumnSpec = {
    volumeKey: M.nb_likes_last_week.key,
    defaultSortKey: M.nb_likes_last_week.key,
    volumeGrowthFallback: M.nb_likes_all.key,
    sortableKeys: SORTABLE_KEYS,
    metaByKey: TIKTOK_METADATA_BY_KEY,
};

/**
 * The backend flags high/very-high-engagement + low/very-low-paid creators
 * (`investment_opportunity` on the raw row — same thresholds as the explore
 * view's health stats). Flagged rows get an "Investment opportunity" badge; its
 * tooltip carries the health-stat detail (level + percentage) and the why.
 * The LLM summary uses the same phrase (aligned in system.md).
 */
function creatorBadge(rawRow: RawRow | undefined): RowBadge | undefined {
    if (!rawRow?.investment_opportunity) return undefined;
    const healthStats = getCreatorHealthStats(rawRow);
    const stat = (
        name: string,
        s?: { scoreLabelShort?: string; metric?: string } | null,
    ): string =>
        `${name}: ${s?.scoreLabelShort ?? '—'}${s?.metric ? ` (${s.metric})` : ''}`;
    return {
        label: 'Investment opportunity',
        title:
            `${stat('Engagement', healthStats?.['engagement: all time'])} · ` +
            `${stat('Paid', healthStats?.['paid views: all time'])}. ` +
            'High engagement with little paid content — a strong boost or ' +
            'paid-partnership candidate.',
    };
}

function buildCreatorsTableModel({ input, output }: VisualProps): TableModel {
    // Prefer the backend-resolved sort (default: likes latest week); fall back to an
    // explicit input sort. Drives the second column + the sort arrow.
    const orderBy = output?.result?.order_by ?? input?.order_by;
    const columns = buildColumns(orderBy, COLUMN_SPEC);
    return {
        breadcrumb: buildBreadcrumb(output, TYPE_LABEL),
        title: {
            parent: parentName(output),
            noun: 'creators',
        },
        link: output?.result?.dashboard_url,
        nameColumnHeader: 'Creator',
        // Rows are @handles, not entity names — a handle's casing is its own.
        preserveNameCase: true,
        columns,
        rows: buildRows(
            output,
            columns,
            NAME_KEY,
            TIKTOK_METADATA_BY_KEY,
            undefined,
            // Link the creator name out to their TikTok profile.
            (rawRow) =>
                typeof rawRow?.creator_url === 'string'
                    ? rawRow.creator_url
                    : undefined,
            creatorBadge,
        ),
    };
}

const CreatorsInlineTable = ({ Frame, dense, ...props }: VisualProps) => (
    <InlineTable
        {...buildCreatorsTableModel(props)}
        Frame={Frame}
        dense={dense}
    />
);

const widget: VisualWidget = {
    match: (input) =>
        input?.data_source === 'tiktok' &&
        input?.entity_relationship === 'creators',
    Component: CreatorsInlineTable,
};

export default widget;
